// @ts-nocheck
import { Box } from '@mui/material';
import { useLoaderData } from 'react-router';
import { useEffect, useState, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Header from './Header/Header';
import TravelDocSkeleton from './TravelDocSkeleton/TravelDocSkeleton';
import { getTravelDoc } from '../../../../services/trip/trip';
import DaySection from './DaySection/DaySection';
import ServiceSection from './ServiceSection/ServiceSection';
import LineItemServiceSection from './LineItemServiceSection/LineItemServiceSection';
import AdditionalInformation from './AdditionalInformation/AdditionalInformation';
import Amenities from './Amenities/Amenities';
import CoverPage from './CoverPage/CoverPage';
import { getLineItemServiceType } from '../../../../constants/lineItemServiceTypes';
import { normalizeTravelDate } from '../../../../utils/travelDate';
import { getTripSettings } from '../../../../services/tenant/tenant';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../../../utils/operatorLogo';
import './TravelDoc.css';

export const TRAVEL_DOC_ID = 'travel-doc';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';

const TravelDoc = () => {
  const { tripId } = useLoaderData();
  const { enableRefactoredServices, enableOperatorContactSettings } =
    useFlags();
  const [tripData, setTripData] = useState(null);
  const [isTripLoading, setIsTripLoading] = useState(true);
  const [tripHasError, setTripHasError] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (enableOperatorContactSettings === undefined) return;
    if (hasFetched.current) return; // Prevent multiple fetches

    hasFetched.current = true;

    const fetchTrip = async () => {
      try {
        const [travelDocResult, tripLogoResult] = await Promise.all([
          getTravelDoc(tripId),
          enableOperatorContactSettings
            ? getTripSettings(tripId, TRIP_LOGO_SETTINGCODE).catch(() => null)
            : Promise.resolve(null)
        ]);
        const { data } = travelDocResult;
        if (data && Object.keys(data).length > 0) {
          setTripData(
            withResolvedOperatorLogo(data, {
              tripLogoUrl: parseTripLogoSetting(
                tripLogoResult?.data?.settingvalue
              ),
              enabled: enableOperatorContactSettings
            })
          );
          setTripHasError(false);
        } else {
          setTripHasError(true);
        }
      } catch (error) {
        setTripHasError(true);
      } finally {
        setIsTripLoading(false);
      }
    };

    fetchTrip();
  }, [enableOperatorContactSettings, tripId]);

  if (isTripLoading) {
    return <TravelDocSkeleton />;
  }

  if (!tripData || tripHasError) {
    return null;
  }

  const tripStartDate =
    normalizeTravelDate(tripData?.trip?.startdate) ?? tripData?.trip?.startdate;

  const getTripDetailedDay = (tripdayid) => {
    return tripData?.tripdetaileddays?.find(
      (day) => day.tripdayid === tripdayid
    );
  };

  const getSortedServices = (services = []) => {
    return services?.sort((a, b) => a.sequence - b.sequence);
  };

  const getSortedLineItems = (lineItems = []) => {
    return [...lineItems].sort((a, b) => a.seq - b.seq);
  };
  // ── Shared header + cover used by both render paths ──────────────────────
  const sharedTop = (
    <>
      {/* Cover page — page 1 */}
      <CoverPage
        trip={tripData.trip}
        tenant={tripData.tenant}
        tenantSettings={tripData.tenantSettings}
      />
    </>
  );

  // NEW: Render with LineItems when feature flag is enabled
  if (enableRefactoredServices && tripData?.trip?.tripdays) {
    return (
      <Box className='travel-doc' id={TRAVEL_DOC_ID}>
        {sharedTop}
        {tripData.trip.tripdays.map((tripDay) => {
          // Enrich LineItems with service type metadata
          const enrichedLineItems = (tripDay.lineitem || []).map((item) => {
            const serviceTypeInfo = getLineItemServiceType(
              item.servicetypecode
            );
            return {
              ...item,
              servicetypename: serviceTypeInfo?.name || item.servicetypecode,
              servicetypeicon: serviceTypeInfo?.iconref || 'MapPin'
            };
          });
          const sortedLineItems = getSortedLineItems(enrichedLineItems);

          return (
            <>
              <DaySection
                key={tripDay.tripdayid}
                tripdayid={tripDay.tripdayid}
                daynumber={tripDay.daynumber}
                date={tripStartDate}
                title={tripDay.title}
                description={tripDay.finaldescription || tripDay.description}
                tripdayimage={tripDay.tripdayimage}
                tenantSettings={tripData.tenantSettings}
              >
                {sortedLineItems.length > 0 && (
                  <div className='services-section'>
                    {sortedLineItems.map((lineItem) => (
                      <LineItemServiceSection
                        key={lineItem.lineitemid}
                        lineItem={lineItem}
                        tripid={tripData.trip.tripid}
                        tenantid={tripData.tenant.tenantid}
                        tenantsettings={tripData.tenantSettings}
                        startdate={tripStartDate}
                        daynumber={tripDay.daynumber}
                      />
                    ))}
                  </div>
                )}
              </DaySection>
              <div className='travel-doc__page-break__wrapper'>
                <div className='travel-doc__page-break' />
              </div>
            </>
          );
        })}
        <>
          {tripData?.trip?.content?.length > 0 && (
            <>
              <AdditionalInformation
                content={tripData.trip.content}
                tenantsettings={tripData.tenantSettings}
              />
              <div className='travel-doc__page-break__wrapper'>
                <div className='travel-doc__page-break' />
              </div>
            </>
          )}
        </>

        <Amenities
          tenantsettings={tripData.tenantSettings}
          amenities={tripData.amenities}
        />
      </Box>
    );
  }

  // LEGACY: Render with TripDetailedDayServices when feature flag is disabled
  return (
    <Box className='travel-doc' id={TRAVEL_DOC_ID}>
      <Header
        id={`${TRAVEL_DOC_ID}-header`}
        tenant={tripData.tenant}
        tenantSettings={tripData.tenantSettings}
        trip={tripData.trip}
      />
      {tripData?.trip?.tripdays?.map((tripdDay) => {
        const detailedDay = getTripDetailedDay(tripdDay.tripdayid);
        const dayServices = detailedDay?.tripdetaileddayservice || [];
        const sortedServices = getSortedServices(dayServices);
        const description = detailedDay?.description || tripdDay.description;

        return (
          <DaySection
            {...detailedDay}
            tenantSettings={tripData?.tenantSettings}
            key={detailedDay.tripdayid}
            date={tripStartDate}
            title={tripdDay?.title}
            description={description}
            tripdayimage={tripdDay?.tripdayimage}
          >
            <div className='services-section'>
              {sortedServices?.map((service) => (
                <ServiceSection
                  {...service}
                  tripid={tripData?.trip?.tripid}
                  tenantid={tripData?.tenant?.tenantid}
                  tenantsettings={tripData?.tenantSettings}
                  key={service?.tripdetaileddayserviceid}
                  startdate={tripStartDate}
                  daynumber={tripdDay?.daynumber}
                />
              ))}
            </div>
          </DaySection>
        );
      })}
      <AdditionalInformation
        content={tripData.trip.content}
        tenantsettings={tripData.tenantSettings}
      />
      <Amenities
        tenantsettings={tripData.tenantSettings}
        amenities={tripData.amenities}
      />
    </Box>
  );
};

TravelDoc.propTypes = {};

export default TravelDoc;
